<template>
    <div class="mb-3">
        <!-- Mirrors the "Complete within [N] [unit]" rhythm: label + interval + frequency. -->
        <div class="flex flex-wrap items-center gap-2">
            <span class="w-32 shrink-0 text-muted">
                {{
                    modelValue.repeat === 'once'
                        ? trans('quality_assurance.configuration.modal.repeat.once_label')
                        : trans('quality_assurance.configuration.modal.repeat.every_label')
                }}
            </span>
            <!-- Reserve the number column so the frequency dropdown stays aligned with the deadline row even when "Once" hides the input. -->
            <div class="w-20 shrink-0">
                <input
                    v-if="modelValue.repeat !== 'once'"
                    v-model="modelValue.interval"
                    class="form-control w-full"
                    min="1"
                    name="interval"
                    type="number"
                    @blur="handleBlur"
                />
            </div>
            <div class="w-40">
                <jet-select
                    v-model="modelValue.repeat"
                    :enable-search="false"
                    :horizontal="false"
                    :options="options"
                    :required="true"
                    form-group-classes="mb-0"
                    @update:model-value="handleRepeatChange"
                />
            </div>
        </div>

        <div v-if="modelValue.repeat === 'weekly'" class="mt-2">
            <form-group :horizontal="false" :label="trans('general.day.label')" label-classes="required">
                <multi-select-dropdown
                    v-model="modelValue.weekdays"
                    :invalid-feedback="weekdaysError"
                    :label="selectedWeekdaysLabel"
                    :options="weekdays"
                    :required="true"
                    classes="w-100"
                />
            </form-group>
            <small class="text-muted">{{ trans('quality_assurance.configuration.modal.repeat.weekday_hint') }}</small>
        </div>
    </div>
</template>

<script setup>
import { computed, defineEmits } from 'vue';
import { trans } from '@/mixins/i18n';
import JetSelect from '@/Jetstream/Select.vue';
import FormGroup from '@/Components/FormGroup.vue';
import MultiSelectDropdown from '@/Components/MultiSelectDropdown.vue';

const emit = defineEmits(['update:modelValue']);

const props = defineProps({
    label: String,
    options: Array,
    form: Object,
    modelValue: {
        type: Object,
        default: () => ({
            repeat: 'once',
            interval: 1,
            weekdays: [],
            count: 1,
            until: null,
            stopCondition: 'never',
        }),
    },
    horizontal: Boolean,
    required: Boolean,
    invalidFeedback: String,
});

const getWeekDay = (day, format = 'long') => {
    return new Intl.DateTimeFormat('en-US', { weekday: format }).format(
        new Date(2024, 0, ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].indexOf(day) + 1),
    );
};

const weekdays = [
    { value: 'monday', label: getWeekDay('monday') },
    { value: 'tuesday', label: getWeekDay('tuesday') },
    { value: 'wednesday', label: getWeekDay('wednesday') },
    { value: 'thursday', label: getWeekDay('thursday') },
    { value: 'friday', label: getWeekDay('friday') },
    { value: 'saturday', label: getWeekDay('saturday') },
    { value: 'sunday', label: getWeekDay('sunday') },
];

const weekdaysError = computed(() => {
    const keys = Object.keys(props.form.errors);
    const firstErrorKey = keys.filter((key) => key.startsWith('recurrence.weekdays'))[0];
    return firstErrorKey ? props.form.errors[firstErrorKey] : null;
});

const selectedWeekdaysLabel = computed(() => {
    if (!props.modelValue.weekdays || props.modelValue.weekdays.length === 0) {
        return trans('general.form.select.placeholder');
    }
    return (props.modelValue.weekdays || []).map((day) => getWeekDay(day, 'short')).join(', ');
});

const handleBlur = (event) => {
    const parsed = Number(event.target.value);
    const value = Number.isFinite(parsed) && parsed > 0 ? Math.floor(parsed) : 1;

    event.target.value = String(value);

    emit('update:modelValue', {
        ...props.modelValue,
        [event.target.name]: value,
    });
};

// The end condition lives in RecurrenceEndCondition but shares this model;
// keep it when switching between recurring cadences, reset it only when the
// schedule stops repeating.
const handleRepeatChange = (option) => {
    emit('update:modelValue', {
        ...props.modelValue,
        repeat: option,
        interval: 1,
        weekdays: [],
        ...(option === 'once' ? { count: 1, until: null, stopCondition: 'never' } : {}),
    });
};
</script>
